import { Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Screen } from "@/components/screen";
import { Card, Pill } from "@/components/ui";
import { useToast } from "@/contexts";
import { getCompetitionById } from "@/features/competitions/api";
import { getTeams, joinTeam } from "@/features/competitions/teams";
import type { CompetitionSummary, TeamScoring, Team as TeamType } from "@/types";

export default function CompetitionTeams() {
  const { id } = useParams({ from: "/competition/$id/teams" }) as { id: string };
  const toast = useToast();
  const [competition, setCompetition] = useState<CompetitionSummary | null>(null);
  const [teams, setTeams] = useState<TeamType[]>([]);
  const [loading, setLoading] = useState(true);
  const [_teamName, _setTeamName] = useState("");

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [compResult, teamsResult] = await Promise.all([getCompetitionById(id), getTeams(id)]);
        if (compResult.data) setCompetition(compResult.data);
        setTeams(teamsResult);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  async function _handleJoinTeam(teamId: string) {
    if (!id) return;
    try {
      await joinTeam(id, teamId);
      toast.showAlert("Joined team", "You joined the team!", "success");
      const data = await getTeams(id);
      setTeams(data);
    } catch (err: unknown) {
      toast.showAlert("Error", err instanceof Error ? err.message : "Failed to join team", "error");
    }
  }

  const teamScoring: TeamScoring = competition?.teamScoring ?? "sum";
  const sorted = [...teams].sort((a, b) => {
    const aVal = teamScoring === "average" ? (a.avgSteps ?? 0) : a.totalSteps;
    const bVal = teamScoring === "average" ? (b.avgSteps ?? 0) : b.totalSteps;
    return bVal - aVal;
  });

  return (
    <Screen
      backTo="/dashboard"
      eyebrow="Teams"
      title="Teams"
      subtitle="View teams, their rankings, and join or create your own."
    >
      {loading && (
        <div className="animate-pulse flex flex-col gap-2">
          {Array.from({ length: 3 }, (_, i) => i).map((n) => (
            <div
              key={n}
              className="bg-card rounded-[20px] p-4 h-20 shadow-[1px_1px_0px_0px_#1c1b2e] border border-[rgba(28,27,46,0.2)]"
            />
          ))}
        </div>
      )}

      {!loading && sorted.length === 0 && (
        <Card>
          <div className="flex flex-col items-center gap-2 py-4">
            <span className="text-xl">👥</span>
            <span className="text-text-muted text-sm italic text-center">
              No teams yet in this competition.
            </span>
          </div>
        </Card>
      )}

      {sorted.map((team, idx) => (
        <Link key={team.id} to={`/competition/${id}/teams/${team.id}`} className="no-underline">
          <div className="bg-card rounded-[20px] p-4 shadow-[1px_1px_0px_0px_#1c1b2e] border border-[rgba(28,27,46,0.2)] flex flex-col gap-2 hover:bg-card-strong transition-colors">
            <div className="flex flex-row items-center gap-3">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-extrabold shadow-[1px_1px_0px_0px_#1c1b2e] border border-text ${
                  idx === 0
                    ? "bg-[#fbbf24]"
                    : idx === 1
                      ? "bg-[#94a3b8]"
                      : idx === 2
                        ? "bg-[#fb7185]"
                        : "bg-purple"
                }`}
              >
                {idx + 1}
              </div>
              <div className="flex-1 flex items-center gap-2 min-w-0">
                <span className="text-text text-base font-extrabold truncate">{team.name}</span>
                {team.group && <Pill label={team.group.name} tone="accent" />}
              </div>
              <div className="flex flex-row items-center gap-3">
                <span className="text-text-dim text-xs font-bold">{team.memberCount} members</span>
                <div className="text-right">
                  {teamScoring === "average" && (
                    <span className="text-text-dim text-[0.625rem] font-bold block leading-tight">
                      {team.avgSteps?.toLocaleString()} avg
                    </span>
                  )}
                  <span className="text-text font-extrabold text-sm">
                    {team.totalSteps.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Link>
      ))}
    </Screen>
  );
}
